import React from 'react';
import PropTypes from 'prop-types';
import Immutable from 'seamless-immutable';
import { compose } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { withHandlers } from 'recompose';
import { connect } from 'react-redux';
import cc from 'classcat';
import { Link as ScrollLink } from 'react-scroll';
import RouterLink from 'redux-first-router-link';

import { interpolateFields } from '#src/lib/env-interpolation.js';

import { customThemes } from '../../lib/themes';
import { selectFields } from '../../lib/contentful/selectors';
import { selectTheme } from '../../store/theme/selectors';

import { linkType, TYPES } from './helpers/types.js';
import { useRowContextParam } from './helpers/useRowContextParam.js';
import styles from './styles.module.scss';

export const LINK_THEMES = Immutable(
  customThemes(['none', 'membership', 'kickstarter', 'transformer', 'landing'])
);

const Link = ({
  theme,
  to = '',
  href, // alias of `to`
  link,
  redirect,
  replace, // alias of `redirect`
  disabled,
  handleClick,
  onClick,
  handleDisabledClick,
  className,
  children,
  name,
  target,
  id,
  currentProduct,
  style,
}) => {
  const withRowContext = useRowContextParam(link);
  const linkFields = link
    ? selectFields(interpolateFields(withRowContext))
    : null;
  let element = RouterLink;
  const themeToUse =
    theme == 'none' ? theme : linkFields?.theme || theme || currentProduct;
  const props = {
    name,
    style,
    target,
    onClick: disabled ? handleDisabledClick : handleClick,
    className: cc([
      { [styles[themeToUse]]: themeToUse != 'none' },
      styles.link,
      className,
    ]),
    id: linkFields?.id || id,
  };

  // alias href to `to` prop
  if (href) {
    to = href;
  }

  if (linkFields) {
    to = linkFields.link;
    if (isNilOrEmpty(children) && linkFields.label) children = linkFields.label;
  }

  const type = linkType(to || onClick);

  switch (type) {
    case TYPES.MAILTO:
    case TYPES.FULL_URL:
      element = 'a';
      Object.assign(props, { href: to });
      break;
    case TYPES.HASH_LINK:
      element = ScrollLink;
      Object.assign(props, {
        to: to.substring(1),
        href: to,
        smooth: 'easeInOutQuint',
        duration: 500,
        offset: -20,
      });
      break;
    case TYPES.CALLBACK:
      element = 'a';
      break;
    // ACTION, RELATIVE_URL
    default:
      Object.assign(props, { to, replace, redirect });
  }

  return React.createElement(element, props, children);
};

Link.propTypes = {
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  link: PropTypes.shape({
    link: PropTypes.string,
    label: PropTypes.string,
    id: PropTypes.string,
  }),
  redirect: PropTypes.bool,
  replace: PropTypes.bool, // alias of `redirect`
  theme: PropTypes.oneOf(LINK_THEMES),
  onClick: PropTypes.func,
  className: PropTypes.string,
  id: PropTypes.string,
  target: PropTypes.string,
};

Link.defaultProps = {
  theme: 'waybetter',
};

const handlers = {
  handleDisabledClick: () => (event) => event.preventDefault(),
  handleClick:
    ({ id, link, onClick, currentProduct }) =>
    (event) => {
      id = id || link?.fields?.id;
      id &&
        analytics.track('Clicked', {
          label: id,
          category: currentProduct,
        });
      onClick && onClick(event);
    },
};

export default compose(
  connect((state) => ({ currentProduct: selectTheme(state) })),
  withHandlers(handlers)
)(Link);
